import { isApiError } from '../../api/client'

/** Review actions handled by the reason dialog (plus `correct`, which has its own dialog). */
export type ReviewDialogAction = 'submit' | 'approve' | 'publish' | 'return' | 'retract' | 'release'

/** FORM-009: action-specific explanation shown in the reason dialog. */
export const ACTION_EXPLANATION: Record<ReviewDialogAction, string> = {
  submit:
    'Submitting locks the content for an independent review. Anyone who contributed to this draft cannot approve it.',
  approve:
    'Approval records the independent check. It does not publish: clients see nothing until a reviewer publishes this version.',
  publish:
    'Publication makes this version visible to subscribing clients and makes it immutable. Later changes need a corrected version.',
  return:
    'Returning sends the draft back to its contributors for changes and clears any approval. Earlier published versions stay visible.',
  retract:
    'Retraction withdraws this published version from client view. It stays in the version history and the audit trail.',
  release:
    'Release copies the current client-visible fields to subscribing clients. Later edits stay internal until the next release.',
}

/** FORM-026: say what did and did not happen. */
export const ACTION_TOAST: Record<ReviewDialogAction | 'correct', string> = {
  submit: 'Submitted for review. Nothing is visible to clients yet.',
  approve: 'Approved. Not yet published: clients see nothing until publication.',
  publish: 'Published. The released record is now immutable.',
  return: 'Returned for changes. The approval was cleared.',
  retract: 'Retracted. Clients no longer see this version; the history is kept.',
  release: 'Released. Subscribing clients now see these fields.',
  correct:
    'Corrected version created as a draft. The published version stays visible until the correction is published.',
}

export const REASON_LABEL: Record<ReviewDialogAction, string> = {
  submit: 'Note for the reviewer',
  approve: 'Review reason',
  publish: 'Publication reason',
  return: 'What needs to change',
  retract: 'Retraction reason',
  release: 'Release reason',
}

export const INDEPENDENCE_MESSAGE =
  'You contributed to this draft, so another reviewer must approve it.'
export const NOT_ASSIGNED_MESSAGE = 'Only the assigned case reviewer may release this record.'

/** Why the caller cannot review, from `blocked_reason` or a refused request. */
export function blockedMessage(reason: string | null | undefined): string | null {
  if (reason === 'not_independent' || reason === 'independence') return INDEPENDENCE_MESSAGE
  if (reason === 'not_assigned') return NOT_ASSIGNED_MESSAGE
  if (reason === 'not_approver') return 'Only the reviewer who approved may publish this record.'
  return null
}

/** Readable message for a refused review action (FORM-027). */
export function reviewErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    const details = error.details as { reason?: string } | undefined
    const specific = blockedMessage(details?.reason)
    if (specific) return specific
    if (error.code === 'stale_revision') {
      return 'This record changed since you opened it. Close this dialog and reload the latest version.'
    }
    return error.message
  }
  return error instanceof Error ? error.message : 'Something went wrong. Nothing was saved.'
}
